import { Injectable } from '@angular/core';
import { Quote, RiskReturn, VixSnapshot } from './dataInterfaces';
import { takeUntil } from 'rxjs/operators';
import { MarketDataService, PricingService } from '../globalData';
import { BehaviorSubject, Subject } from 'rxjs';
import { faCaretUp } from '@fortawesome/free-solid-svg-icons/faCaretUp';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons/faCaretDown';
import { UsersUtil } from './users.util';
import { Auth } from '../auth.service';
import { environment } from '../../environments/environment';
import { MobileUtil } from './mobileUtil.service';
import { DeviceDetectorService } from 'ngx-device-detector';

@Injectable()
export class MarketInfoUtil {

  private readonly onDestroy: Subject<void> = new Subject<void>();

  static readonly SBBI_URL = 'https://www.cfainstitute.org/-/media/documents/book/rf-publication/2021/sbbi-summary-edition-2021.pdf';

  customVix: number;
  loadingCurrentVix: BehaviorSubject<boolean> = new BehaviorSubject<boolean>( true );
  currentVixError: boolean = false;

  marketInfoSettings: any = {};
  currentVixQuote: Quote;
  snapshot: VixSnapshot;

  static readonly defaultLongTermRiskPremium: number = 7.7;
  static readonly defaultLongTermStandardDeviation: number = 17.2;
  static readonly defaultLongTermObservations: string = '108';
  static readonly defaultShortTermObservations: string = '60';

  longTermStandardDeviation: number;
  shortTermStandardDeviation: number;
  longTermRiskPremium: number;
  shortTermRiskPremium: number;

  calculatedShortTermRiskPremium: number;

  historicalVixError: boolean = false;
  loadingHistoricalVix: BehaviorSubject<boolean> = new BehaviorSubject<boolean>( true );
  vixSeriesStats: any;
  vixSeries: any[];
  currentDateRangeOption: any;

  dateRangeRadioOptions: any[] = [
    // { value: { unitsBack: '1', unit: 'D' }, label: '1D' }, // comment out until we get intraday numbers
    { value: { unitsBack: '1', unit: 'W' }, label: '1W' },
    { value: { unitsBack: '1', unit: 'M' }, label: '1M' },
    { value: { unitsBack: '1', unit: 'Y' }, label: '1Y' },
    { value: { unitsBack: '3', unit: 'Y' }, label: '3Y' },
    { value: { unitsBack: '5', unit: 'Y' }, label: '5Y' },
    { value: { unitsBack: '10', unit: 'Y' }, label: '10Y' },
    { value: { unitsBack: '15', unit: 'Y' }, label: '15Y' },
    { value: { unitsBack: '1', unit: 'MAX' }, label: 'MAX' },

  ];

  deviceIsMobile: boolean = false;

  constructor( private _mdService: MarketDataService,
               private _pricingService: PricingService,
               private _detectorService: DeviceDetectorService,
               private _auth: Auth ) {

    this.deviceIsMobile = MobileUtil.deviceIsMobile( this._detectorService );
    const userPrefs = UsersUtil.checkPreferences( this._auth, 'marketInfo' );
    this.marketInfoSettings = userPrefs?.marketInfo ?? {};
    this.setLocalMarketInfoSettings();

    if ( environment.env !== 'prod' ) {
      window[ 'ripsaw_marketInfoUtil' ] = this;
    }

  }

  marketSettingsChanged( settings: any ) {
    this.marketInfoSettings = settings;
    this.setLocalMarketInfoSettings();
  }

  /**
   * reusable function for setting teh current vix quote and any fields dependent upon it
   * @param quote
   */
  setCurrentVix( quote: Quote ) {
    this.currentVixQuote = quote;
    const vixToUse = this.customVix ?? this.currentVixQuote?.price;
    const diffFromLTAV: number = vixToUse - this.longTermStandardDeviation;
    const diffPercent: number = diffFromLTAV / this.longTermStandardDeviation;
    this.snapshot = {
      current: vixToUse,
      previousClose: this.currentVixQuote?.lastClose,
      change: Math.abs( vixToUse - this.currentVixQuote?.lastClose ),
      percentChange: Math.abs( ( vixToUse - this.currentVixQuote?.lastClose ) / this.currentVixQuote?.lastClose ),
      diffFromLTAV: Math.abs( diffFromLTAV ),
      percentDiffFromLTAV: Math.abs( diffPercent ),
      currentColor: MarketInfoUtil.getColor( this.currentVixQuote?.price ),
      icon: vixToUse - this.currentVixQuote?.lastClose >= 0 ? faCaretUp : faCaretDown,
      LTAVicon: vixToUse - this.longTermStandardDeviation >= 0 ? faCaretUp : faCaretDown,
      diffDirection: diffFromLTAV > 0 ? 'up' : diffFromLTAV < 0 ? 'down' : '',
    };

    this.loadingCurrentVix.next( false );
    this.setLocalMarketInfoSettings();
    this.currentVixError = false;
  }

  getCurrentVix(): void {
    this.loadingCurrentVix.next( true );
    this.currentVixError = false;
    this.customVix = this.marketInfoSettings?.shortTermMarketVolatility?.value; // if it isn't set, this should just be undefined
    this.currentVixError = false;
    this._pricingService.getPrice( 'VIX', 'INDX' )
      .pipe( takeUntil( this.onDestroy ) )
      .subscribe( {
        next: ( resp: any ) => {
          if ( !resp?.data?.price ) {
            // no price returned
            this.currentVixError = true;
          } else {
            this.setCurrentVix( resp?.data );
          }
        }, error: ( err ) => {
          console.error( err );
          this.currentVixError = true;
          this.loadingCurrentVix.next( false );
        },
      } );
  }

  setHistoricalVIX( data: any ): void {
    this.vixSeries = data; // .slice( 0, 3750 );
    this.vixSeriesStats = data;
    this.currentDateRangeOption = this.deviceIsMobile ? this.dateRangeRadioOptions[ 3 ]?.value : this.dateRangeRadioOptions[ 6 ]?.value;
    this.loadingHistoricalVix.next( false );
  }

  getHistoricalVix(): void {
    this.loadingHistoricalVix.next( true );
    this.historicalVixError = false;
    this._mdService.getHistoricalVix()
      .pipe( takeUntil( this.onDestroy ) )
      .subscribe( {
        next: ( resp: any ) => {
          this.setHistoricalVIX( resp?.data );
          // this.checkForBothDataPieces();
        }, error: ( err ) => {
          console.error( err );
          this.historicalVixError = true;
          this.loadingHistoricalVix.next( false );
        },
      } );
  }

  setLocalMarketInfoSettings() {
    // LONG TERM MARKET VOL
    if ( this.marketInfoSettings?.longTermMarketVolatility?.value ) {
      this.longTermStandardDeviation = this.marketInfoSettings?.longTermMarketVolatility?.value;
    } else {
      this.longTermStandardDeviation = MarketInfoUtil.defaultLongTermStandardDeviation;
    }

    // LONG TERM RISK PREMIUM
    if ( this.marketInfoSettings?.longTermMarketRiskPremium?.value ) {
      this.longTermRiskPremium = this.marketInfoSettings?.longTermMarketRiskPremium?.value;
    } else {
      this.longTermRiskPremium = MarketInfoUtil.defaultLongTermRiskPremium;
    }

    // SHORT TERM MARKET VOL
    if ( this.marketInfoSettings?.shortTermMarketVolatility?.value ) {
      this.shortTermStandardDeviation = this.marketInfoSettings?.shortTermMarketVolatility.value;
    } else {
      this.shortTermStandardDeviation = this.currentVixQuote?.price;
    }

    // SHORT TERM RISK PREMIUM
    if ( this.marketInfoSettings?.shortTermMarketRiskPremium?.value ) {
      this.shortTermRiskPremium = this.marketInfoSettings?.shortTermMarketRiskPremium?.value;
    } else {
      this.shortTermRiskPremium = this.calcShortTermRiskPremium();
    }

    this.calculatedShortTermRiskPremium = this.calcShortTermRiskPremium();

  }

  calcShortTermRiskPremium() {
    return parseFloat( ( ( this.longTermRiskPremium / Math.pow( this.longTermStandardDeviation, 2 ) ) * Math.pow( this.shortTermStandardDeviation, 2 ) ).toFixed( 2 ) );
  }


  static stockRiskReturnsHaveError( riskReturnsArray: RiskReturn[] ) {
    for ( const rr of riskReturnsArray ) {
      if ( rr.identifier === 'STOCKS' && (
        rr.expectedReturn === undefined ||
        rr.updatedStandardDeviation === undefined ||
        rr.expectedSharpeRatio === undefined ||
        rr.longTermUpdatedStandardDeviation === undefined ) ) {
        return true;
      }
    }
    return false;
  }

  static bondRiskReturnsHaveError( riskReturnsArray: RiskReturn[] ) {
    for ( const rr of riskReturnsArray ) {
      if ( rr.identifier === 'BONDS' && (
        rr.expectedReturn === undefined ||
        rr.updatedStandardDeviation === undefined ||
        rr.annualizedStandardDeviation === undefined ||
        rr.expectedSharpeRatio === undefined ||
        rr.longTermExpectedReturn === undefined ||
        rr.longTermAnnualizedStandardDeviation === undefined ) ) {
        return true;
      }
    }
    return false;
  }

  static getColor( value: number ) {
    if ( 0 <= value && value <= 10 ) {
      return '#49a1e0';
    } else if ( 10 < value && value <= 13 ) {
      return '#207bbc';
    } else if ( 13 < value && value <= 24 ) {
      return '#ffa139';
    } else if ( 24 < value && value <= 48 ) {
      return '#ff452b';
    } else if ( value > 48 ) {
      return '#993333';
    }
  }
}
